'use strict';

import {
    Buildings,
    GSAPAnimationMixer,
    isMobile,
    projectMode,
    resourcesFolder, settingsData,
    threeJsVariables,
} from '../../settings';
import {
    BufferGeometry,
    FrontSide,
    ShaderMaterial,
    Color,
    Group,
    BufferAttribute,
    Mesh,
    MeshStandardMaterial,
    Vector2,
    RepeatWrapping,
    Vector3,
    Shape,
    ShapeGeometry,
    Float32BufferAttribute,
    CatmullRomCurve3,
    PointsMaterial,
    Points,
    LinearSRGBColorSpace,
    CubeTextureLoader,
    TextureLoader,
    Box3,
    Sphere
} from 'three';
import { PlanarUV } from '../../libs/PlanarUV';
import { OrbitDayAndNight } from '../liveAnimations/DayNight/OrbitDayAndNight';
import { latLngAltToVector3 } from '../components/publicLayers/Layer';
import { Cloudflare } from '../api/Cloudflare';
import { OutlineEdges } from '../../libs/outlineEdges/OutlineEdges';
import { BaseElement } from '../interfaces/BaseElement';
import { Switch } from '../interfaces/switchers/Switch';
import { changeLangToCurrent } from '../components/langSwitcher/langSwitcher';
import { Terrain } from '../components/Terrain/Terrain';
import { DisplayOptions } from '../components/displayOptions/DisplayOptions';
import { Weather } from '../components/weather/Weather';
import { ColorCorrection } from './colorCorrection';
import { Loader } from './loader/Loader';
import { LUT } from '../post-processing/Passes/LUT';
import backgroundVertex from '../threejs/Shaders/background/vertex.glsl';
import backgroundFragment from '../threejs/Shaders/background/fragment.glsl';
import { OrbitSea } from '../liveAnimations/DayNight/sea/OrbitSea';
import { isCylinderDisplayModeEqual, userPrevent } from '../../functions';
import { GroundProjectedSkybox2 } from './GroundProjectedSkybox2';
import { DreamsPreloader } from '../components/preloader/DreamsPreloader';
import gsap from 'gsap';
import { UpdateTask } from '../gui/LoaderTask';
import { Environment } from '../components/Environment/Environment';
import {ViewMode} from "../../common";
import { BmbyMode } from '../mode/BmbyMode';
import { PublicLayers } from '../components/publicLayers/PublicLayers';
// import * as dat from 'dat.gui';


const _sphereBox = new Box3();
const _sphereClamped = new Vector3();
const _sphere = new Sphere();
const _cameraTarget = new Vector3();

const backgroundShader = new ShaderMaterial({
    uniforms: {
        map1        : { value: null        },
        map2        : { value: null        },
        alphaMap    : { value: null        },
        useAlphaMap : { value: false       },
        t           : { value: 0.0         },
        fogColor    : { value: new Color() },
        fogDensity  : { value: 0.0         },
        vantage     : { value: false       },
        uLod       : { value: 0.0         },
        lutSize: { value: 0.0},
        lutIntensity: { value: 0.0},
        lut3d: { value: null },
        lut: { value: null },
        cloudsActive: {value: 0.0},
        clouds: {value: new Color().setStyle('#ffffff',LinearSRGBColorSpace)},
        cloudsScale: {value: 2.7},
        cloudsDark: {value: 0.0},
        cloudsLight: {value: 0.16},
        cloudsCover: {value: 0.5},
        cloudsAlpha: {value: 4.0},
        cloudsTint: {value: 0.91},
        horizon: {value: 0.0},
        uSpeed: {value: 0.0},
        uTime: {value: 0.0}
    },
    defines: {
        USE_3DTEXTURE: 1
    },
    vertexShader: backgroundVertex,
    fragmentShader: backgroundFragment,
    side        : FrontSide,
    depthTest   : true,
    depthWrite  : false,
    transparent : true,
});

const v_normalize = (a) => {
    const d = Math.sqrt(a[0]*a[0]+a[1]*a[1]+a[2]*a[2]);
    a[0] = a[0]/d;
    a[1] = a[1]/d;
    a[2] = a[2]/d;
};

export const generateRegion = (vx,vy,vz) => {
    let w = 500;
    let hw = w/2;
    let a1 = [0,0,0];
    let a2 = [0,0,0];
    let a3 = [0,0,0];
    let a4 = [0,0,0];
    let v1 = [1,0,0];
    let v2 = [0,0,1];
    let v3 = [0,1,0];
    let b1 = [0,0,0];
    let b2 = [0,0,0];
    let b3 = [0,0,0];
    let b4 = [0,0,0];
    a1[0] = vz[0]*hw - vx[0]*hw + vy[0]*hw;
    a1[1] = vz[1]*hw - vx[1]*hw + vy[1]*hw;
    a1[2] = vz[2]*hw - vx[2]*hw + vy[2]*hw;
    a2[0] = vz[0]*hw + vx[0]*hw + vy[0]*hw;
    a2[1] = vz[1]*hw + vx[1]*hw + vy[1]*hw;
    a2[2] = vz[2]*hw + vx[2]*hw + vy[2]*hw;
    a3[0] = vz[0]*hw + vx[0]*hw - vy[0]*hw;
    a3[1] = vz[1]*hw + vx[1]*hw - vy[1]*hw;
    a3[2] = vz[2]*hw + vx[2]*hw - vy[2]*hw;
    a4[0] = vz[0]*hw - vx[0]*hw - vy[0]*hw;
    a4[1] = vz[1]*hw - vx[1]*hw - vy[1]*hw;
    a4[2] = vz[2]*hw - vx[2]*hw - vy[2]*hw;

    //  a1   a2
    //
    //  a4   a3
    //
    let count = 16;
    let delta = 1/count;
    v2[0] = vx[0]*w*delta;
    v2[1] = vx[1]*w*delta;
    v2[2] = vx[2]*w*delta;
    v3[0] = vy[0]*w*delta;
    v3[1] = vy[1]*w*delta;
    v3[2] = vy[2]*w*delta;

    let buffp = new Array();
    let buffu = new Array();
    let u1 = [0,0];
    let u2 = [0,0];
    let u3 = [0,0];
    let u4 = [0,0];
    for (let y=0;y<count;y++) {
        for (let x=0;x<count;x++) {

            b1[0] = a1[0] - v3[0]*y + v2[0]*x;
            b1[1] = a1[1] - v3[1]*y + v2[1]*x;
            b1[2] = a1[2] - v3[2]*y + v2[2]*x;
            b2[0] = b1[0] + v2[0];
            b2[1] = b1[1] + v2[1];
            b2[2] = b1[2] + v2[2];
            b3[0] = b1[0] - v3[0];
            b3[1] = b1[1] - v3[1];
            b3[2] = b1[2] - v3[2];
            b4[0] = b2[0] - v3[0];
            b4[1] = b2[1] - v3[1];
            b4[2] = b2[2] - v3[2];

            v_normalize(b1);
            v_normalize(b2);
            v_normalize(b3);
            v_normalize(b4);
            b1[0] = b1[0]*w;
            b1[1] = b1[1]*w;
            b1[2] = b1[2]*w;
            b2[0] = b2[0]*w;
            b2[1] = b2[1]*w;
            b2[2] = b2[2]*w;
            b3[0] = b3[0]*w;
            b3[1] = b3[1]*w;
            b3[2] = b3[2]*w;
            b4[0] = b4[0]*w;
            b4[1] = b4[1]*w;
            b4[2] = b4[2]*w;

            u1[0] = delta*x;
            u1[1] = 1.0 - delta*y;
            u2[0] = u1[0] + delta;
            u2[1] = u1[1];
            u3[0] = u1[0];
            u3[1] = u1[1] - delta;
            u4[0] = u1[0] + delta;
            u4[1] = u1[1] - delta;

            buffp.push(b1[0],b1[1],b1[2]);
            buffp.push(b2[0],b2[1],b2[2]);
            buffp.push(b3[0],b3[1],b3[2]);
            buffp.push(b3[0],b3[1],b3[2]);
            buffp.push(b2[0],b2[1],b2[2]);
            buffp.push(b4[0],b4[1],b4[2]);

            buffu.push(u1[0],u1[1]);
            buffu.push(u2[0],u2[1]);
            buffu.push(u3[0],u3[1]);
            buffu.push(u3[0],u3[1]);
            buffu.push(u2[0],u2[1]);
            buffu.push(u4[0],u4[1]);
        }
    }

    let geometry = new BufferGeometry();
    geometry.setAttribute('position', new BufferAttribute(new Float32Array(buffp),3));
    geometry.setAttribute('uv', new BufferAttribute(new Float32Array(buffu),2));
    let material = backgroundShader.clone();
    return new Mesh(geometry,material);
};

export const prepareSphere = (textureLoader) => {

    // if (OrbitDayAndNight.isActiveAndSelected) return;

    if (projectMode.LiteMode.isActive() && (projectMode.LiteMode.modeIs('picture') || projectMode.LiteMode.modeIs('empty'))) {
        return;
    }

    const {cdnRepository,projectFolder} = config;
    const {cylinderBg,type,projection} = config.resources.background;

    const imgUrl1 = `${cdnRepository}/${projectFolder}/${resourcesFolder}/textures/background`;
    const imgUrl2 = '/assets/model/cylinder';
    const format = '.jpg';
    const isSkyActive = cylinderBg === 'sky';
    const needsOptimization = threeJsVariables.quality.optimizationTasks();

    if (BmbyMode.active && !isSkyActive)
        return;

    let group = new Group();
    group.name = 'Background';
    
    if ((projectMode.AirMode.active && !isSkyActive) || threeJsVariables.orbitBuildingsNavigation.active) {
        group.collisionDetect = () => {};
        threeJsVariables.background = group;
        return;
    }
    
    const lut = LUT.getScope('background');

    let urls = {
        px: Cloudflare.resize(imgUrl1+'/px'+format,{width: 500, quality: 50}),
        nx: Cloudflare.resize(imgUrl1+'/nx'+format,{width: 500, quality: 50}),
        py: Cloudflare.resize(imgUrl1+'/py'+format,{width: 500, quality: 50}),
        ny: Cloudflare.resize(imgUrl1+'/ny'+format,{width: 500, quality: 50}),
        pz: Cloudflare.resize(imgUrl1+'/pz'+format,{width: 500, quality: 50}),
        nz: Cloudflare.resize(imgUrl1+'/nz'+format,{width: 500, quality: 50})
    };

    let urls_alpha = {
        px: Cloudflare.resize(imgUrl1+'/px_alpha'+format,{width: 500, quality: 50}),
        nx: Cloudflare.resize(imgUrl1+'/nx_alpha'+format,{width: 500, quality: 50}),
        py: Cloudflare.resize(imgUrl1+'/py_alpha'+format,{width: 500, quality: 50}),
        ny: Cloudflare.resize(imgUrl1+'/ny_alpha'+format,{width: 500, quality: 50}),
        pz: Cloudflare.resize(imgUrl1+'/pz_alpha'+format,{width: 500, quality: 50}),
        nz: Cloudflare.resize(imgUrl1+'/nz_alpha'+format,{width: 500, quality: 50})
    };
    
    if (!needsOptimization) {
        urls = {
            px: Cloudflare.resize(imgUrl1+'/px'+format,{width: 2048}),
            nx: Cloudflare.resize(imgUrl1+'/nx'+format,{width: 2048}),
            py: Cloudflare.resize(imgUrl1+'/py'+format,{width: 2048}),
            ny: Cloudflare.resize(imgUrl1+'/ny'+format,{width: 2048}),
            pz: Cloudflare.resize(imgUrl1+'/pz'+format,{width: 2048}),
            nz: Cloudflare.resize(imgUrl1+'/nz'+format,{width: 2048})
        };
    
        urls_alpha = {
            px: Cloudflare.resize(imgUrl1+'/px_alpha'+format,{width: 2048}),
            nx: Cloudflare.resize(imgUrl1+'/nx_alpha'+format,{width: 2048}),
            py: Cloudflare.resize(imgUrl1+'/py_alpha'+format,{width: 2048}),
            ny: Cloudflare.resize(imgUrl1+'/ny_alpha'+format,{width: 2048}),
            pz: Cloudflare.resize(imgUrl1+'/pz_alpha'+format,{width: 2048}),
            nz: Cloudflare.resize(imgUrl1+'/nz_alpha'+format,{width: 2048})
        };
    }

    const useAlphaMap = fetch(urls_alpha.px, { method: 'HEAD', mode: 'cors' })
        .then(response => { return response.ok; })
        .catch(() => false);

    if (type === 'projection') {
        const cubeTextureLoader = new CubeTextureLoader();
        const texture = cubeTextureLoader.load(Object.values(urls));
        texture.colorSpace = LinearSRGBColorSpace;
        const mesh = new GroundProjectedSkybox2(texture,{
            radius: projection?.radius || 2000,
            height: projection?.height || 100
        });
        useAlphaMap.then((result) => {
            if (!result) return;
            cubeTextureLoader.load(Object.values(urls_alpha), (texture) => {
                mesh.alphaMap = texture;
            });
        });

        mesh.scale.setScalar( 1000 );
        if (lut) {
            lut.include(mesh.material);
        }

        if (cylinderBg && cylinderBg === 'sky') {
            const secondUrls = [
                imgUrl2+'/px'+format,
                imgUrl2+'/nx'+format,
                imgUrl2+'/py'+format,
                imgUrl2+'/ny'+format,
                imgUrl2+'/pz'+format,
                imgUrl2+'/nz'+format
            ];
            mesh.layers.enable(1);
            const secondTexture = cubeTextureLoader.load(secondUrls);
            secondTexture.colorSpace = LinearSRGBColorSpace;
            mesh.secondTexture = secondTexture;
        }

        if (!isMobile && needsOptimization) {
            const updateTexturesTask = new UpdateTask(() => {
                urls = {
                    px: Cloudflare.resize(imgUrl1+'/px'+format,{width: 2048}),
                    nx: Cloudflare.resize(imgUrl1+'/nx'+format,{width: 2048}),
                    py: Cloudflare.resize(imgUrl1+'/py'+format,{width: 2048}),
                    ny: Cloudflare.resize(imgUrl1+'/ny'+format,{width: 2048}),
                    pz: Cloudflare.resize(imgUrl1+'/pz'+format,{width: 2048}),
                    nz: Cloudflare.resize(imgUrl1+'/nz'+format,{width: 2048})
                };

                urls_alpha = {
                    px: Cloudflare.resize(imgUrl1+'/px_alpha'+format,{width: 2048}),
                    nx: Cloudflare.resize(imgUrl1+'/nx_alpha'+format,{width: 2048}),
                    py: Cloudflare.resize(imgUrl1+'/py_alpha'+format,{width: 2048}),
                    ny: Cloudflare.resize(imgUrl1+'/ny_alpha'+format,{width: 2048}),
                    pz: Cloudflare.resize(imgUrl1+'/pz_alpha'+format,{width: 2048}),
                    nz: Cloudflare.resize(imgUrl1+'/nz_alpha'+format,{width: 2048})
                };

                cubeTextureLoader.load(Object.values(urls),texture => {
                    texture.colorSpace = LinearSRGBColorSpace;
                    let oldTexture = mesh.material.uniforms.map.value;
                    mesh.material.uniforms.map.value = texture;
                    oldTexture.dispose();
                    oldTexture = null;
                });

                useAlphaMap.then((result) => {
                    if (!result) return;
                    cubeTextureLoader.load(Object.values(urls_alpha),texture => {
                        texture.colorSpace = LinearSRGBColorSpace;
                        let oldTexture = mesh.material.uniforms.alphaMap.value;
                        mesh.material.uniforms.alphaMap.value = texture;
                        oldTexture.dispose();
                        oldTexture = null;
                    });
                });
            });
            threeJsVariables.tasks.addTask(updateTexturesTask);
        }

        group.add(mesh);

    } else {

        function mainTextureLoad(loader) {
            loader.load(urls.nz, t=> {
                let oldTexture = m1.material.uniforms.map1.value;
                m1.material.uniforms.map1.value=t;
                if (oldTexture) {
                    oldTexture.dispose();
                    oldTexture = null;
                }
            });
            loader.load(urls.pz, t=> {
                let oldTexture = m2.material.uniforms.map1.value;
                m2.material.uniforms.map1.value=t;
                if (oldTexture) {
                    oldTexture.dispose();
                    oldTexture = null;
                }
            });
            loader.load(urls.px, t=> {
                let oldTexture = m3.material.uniforms.map1.value;
                m3.material.uniforms.map1.value=t;
                if (oldTexture) {
                    oldTexture.dispose();
                    oldTexture = null;
                }
            });
            loader.load(urls.nx, t=> {
                let oldTexture = m4.material.uniforms.map1.value;
                m4.material.uniforms.map1.value=t;
                if (oldTexture) {
                    oldTexture.dispose();
                    oldTexture = null;
                }
            });
            loader.load(urls.py, t=> {
                let oldTexture = m5.material.uniforms.map1.value;
                m5.material.uniforms.map1.value=t;
                if (oldTexture) {
                    oldTexture.dispose();
                    oldTexture = null;
                }
            });
            loader.load(urls.ny, t=> {
                let oldTexture = m6.material.uniforms.map1.value;
                m6.material.uniforms.map1.value=t;
                if (oldTexture) {
                    oldTexture.dispose();
                    oldTexture = null;
                }
            });

            function tryLoadAlphaMap(url, meshMaterial) {
                useAlphaMap.then((result) => {
                    if (!result) return;
                    loader.load(url, t => {
                        let oldTexture = meshMaterial.uniforms.alphaMap.value;
                        meshMaterial.uniforms.alphaMap.value = t;
                        meshMaterial.uniforms.useAlphaMap.value = true;
                        if (oldTexture) {
                            oldTexture.dispose();
                            oldTexture = null;
                        }
                    });
                });
            }

            tryLoadAlphaMap(urls_alpha.nz, m1.material);
            tryLoadAlphaMap(urls_alpha.pz, m2.material);
            tryLoadAlphaMap(urls_alpha.px, m3.material);
            tryLoadAlphaMap(urls_alpha.nx, m4.material);
            tryLoadAlphaMap(urls_alpha.py, m5.material);
            tryLoadAlphaMap(urls_alpha.ny, m6.material);
        }


        let m1 = generateRegion([0,0,-1],[0,1,0],[ 1,0,0]);
        let m2 = generateRegion([0,0, 1],[0,1,0],[-1,0,0]);
        let m3 = generateRegion([ 1,0,0],[0,1,0],[0,0, 1]);
        let m4 = generateRegion([-1,0,0],[0,1,0],[0,0,-1]);
        let m5 = generateRegion([0,0, 1],[1,0,0],[0, 1,0]);
        let m6 = generateRegion([0,0,-1],[1,0,0],[0,-1,0]);
        m1.name = 'nz';
        m2.name = 'pz';
        m3.name = 'px';
        m4.name = 'nx';
        m5.name = 'py';
        m6.name = 'ny';
        m6.rotation.y = Math.PI;
        if (lut) {
            lut.include(m1.material);
            lut.include(m2.material);
            lut.include(m3.material);
            lut.include(m4.material);
            lut.include(m5.material);
            lut.include(m6.material);
        }
        group.add(m1);
        group.add(m2);
        group.add(m3);
        group.add(m4);
        group.add(m5);
        group.add(m6);

        if (projectMode.LiteMode.isActive()){
            textureLoader.load(imgUrl2+'/nz'+format, t => {
                m1.material.uniforms.map1.value = t;
                m1.material.uniforms.map2.value = t;
                isSkyActive && m1.layers.enable(1);
            });
            textureLoader.load(imgUrl2+'/pz'+format, t => {
                m2.material.uniforms.map1.value = t;
                m2.material.uniforms.map2.value = t;
                isSkyActive && m2.layers.enable(1);
            });
            textureLoader.load(imgUrl2+'/px'+format, t => {
                m3.material.uniforms.map1.value = t;
                m3.material.uniforms.map2.value = t;
                isSkyActive && m3.layers.enable(1);

            });
            textureLoader.load(imgUrl2+'/nx'+format, t => {
                m4.material.uniforms.map1.value = t;
                m4.material.uniforms.map2.value = t;
                isSkyActive && m4.layers.enable(1);
            });
            textureLoader.load(imgUrl2+'/py'+format, t => {
                m5.material.uniforms.map1.value = t;
                m5.material.uniforms.map2.value = t;
                isSkyActive && m5.layers.enable(1);
            });
            textureLoader.load(imgUrl2+'/ny'+format, t => {
                m6.material.uniforms.map1.value = t;
                m6.material.uniforms.map2.value = t;
                isSkyActive && m6.layers.enable(1);
            });


        } else {
           mainTextureLoad(textureLoader);

            if (cylinderBg && cylinderBg === 'sky') {
                textureLoader.load(imgUrl2+'/nz'+format, t=> {
                    // threeJsVariables.renderer.initTexture(t);
                    m1.material.uniforms.map2.value=t
                    m1.layers.enable(1);
                });
                textureLoader.load(imgUrl2+'/pz'+format, t=> {
                    // threeJsVariables.renderer.initTexture(t);
                    m2.material.uniforms.map2.value=t
                    m2.layers.enable(1);
                });
                textureLoader.load(imgUrl2+'/px'+format, t=> {
                    // threeJsVariables.renderer.initTexture(t);
                    m3.material.uniforms.map2.value=t
                    m3.layers.enable(1);
                });
                textureLoader.load(imgUrl2+'/nx'+format, t=> {
                    // threeJsVariables.renderer.initTexture(t);
                    m4.material.uniforms.map2.value=t
                    m4.layers.enable(1);
                });
                textureLoader.load(imgUrl2+'/py'+format, t=> {
                    // threeJsVariables.renderer.initTexture(t);
                    m5.material.uniforms.map2.value=t
                    m5.layers.enable(1);
                });
                textureLoader.load(imgUrl2+'/ny'+format, t=> {
                    // threeJsVariables.renderer.initTexture(t);
                    m6.material.uniforms.map2.value=t
                    m6.layers.enable(1);
                });
            }

            if (!isMobile && needsOptimization) {
                const updateTexturesTask = new UpdateTask(() => {
                    urls = {
                        px: Cloudflare.resize(imgUrl1+'/px'+format,{width: 2048}),
                        nx: Cloudflare.resize(imgUrl1+'/nx'+format,{width: 2048}),
                        py: Cloudflare.resize(imgUrl1+'/py'+format,{width: 2048}),
                        ny: Cloudflare.resize(imgUrl1+'/ny'+format,{width: 2048}),
                        pz: Cloudflare.resize(imgUrl1+'/pz'+format,{width: 2048}),
                        nz: Cloudflare.resize(imgUrl1+'/nz'+format,{width: 2048})
                    };

                    urls_alpha = {
                        px: Cloudflare.resize(imgUrl1+'/px_alpha'+format,{width: 2048}),
                        nx: Cloudflare.resize(imgUrl1+'/nx_alpha'+format,{width: 2048}),
                        py: Cloudflare.resize(imgUrl1+'/py_alpha'+format,{width: 2048}),
                        ny: Cloudflare.resize(imgUrl1+'/ny_alpha'+format,{width: 2048}),
                        pz: Cloudflare.resize(imgUrl1+'/pz_alpha'+format,{width: 2048}),
                        nz: Cloudflare.resize(imgUrl1+'/nz_alpha'+format,{width: 2048})
                    };
                    const tl = new TextureLoader();
                    mainTextureLoad(tl);
                });
                threeJsVariables.tasks.addTask(updateTexturesTask);
            }
        }
    }

    const {position,rotation,scale} = config.resources.background;
    group.position.set(position.x,position.y,position.z);
    group.rotation.set(rotation.x,rotation.y,rotation.z);
    group.scale.set(scale.x,scale.y,scale.z);
    
    const initialParams = {
        position: new Vector3().copy(group.position),
        scale: new Vector3().copy(group.scale)
    };
    
    _sphereBox.setFromObject(group);
    _sphereBox.getBoundingSphere(_sphere);
    
    group.collisionDetect = () => {
      if (settingsData.activeViewMode === ViewMode.Orbit && group.visible && group.parent) {
          
          if (!initialParams.position.equals(group.position) || !initialParams.scale.equals(group.scale)) {
              initialParams.position.copy(group.position);
              initialParams.scale.copy(group.scale);
              _sphereBox.setFromObject(group);
              _sphereBox.getBoundingSphere(_sphere);
              _sphere.radius /= 2;
          }
          
          if (!_sphere.containsPoint(threeJsVariables.perspectiveCamera.position)) {
              _sphere.clampPoint(threeJsVariables.perspectiveCamera.position,_sphereClamped);
              _cameraTarget.copy(threeJsVariables.orbitControls.target);
              _cameraTarget.multiplyScalar(0.98);
              _cameraTarget.y = threeJsVariables.orbitControls.target.y;
              threeJsVariables.orbitControls.target.copy(_cameraTarget);
              threeJsVariables.perspectiveCamera.position.copy(_sphereClamped);
              threeJsVariables.perspectiveCamera.lookAt(threeJsVariables.orbitControls.target);
          }
      }
    };

    // const sp = new SphereBufferGeometry(800,16,16);
    // const mat = new MeshStandardMaterial();
    // const mesh = new Mesh(sp,mat);
    // threeJsVariables.scene.add(mesh);


     !OrbitDayAndNight.hasBackgroundToggle && threeJsVariables.scene.add(group);
    threeJsVariables.renderList.reorder();
    //if (cylinderBg && cylinderBg === 'sky')
        threeJsVariables.background = group;
}

export function backgroundHide () {
    if (config.features.privateHouseMode || isCylinderDisplayModeEqual('show all')) return;

    const {cylinderBg} = config.resources.background;
    const mixer = GSAPAnimationMixer.getMixer('cylinderEnter');

    if (cylinderBg && cylinderBg === 'sky'){

        let a = threeJsVariables.background //threeJsVariables.scene.getObjectByName('Background')
        if (!a){
            return
        }

        a.visible = true;

        for (let i=0;i<a.children.length;i++){
            let c = a.children[i]

            // c.material.uniforms.t.value = 1.0;

            const animation = gsap.to(c.material.uniforms.t, {
                value : 1.0,
                duration: GSAPAnimationMixer.duration,
                ease: GSAPAnimationMixer.ease
            });
            mixer.addAnimation(animation);
        }

    } else {

        const bg = threeJsVariables.scene.getObjectByName('Background');
        if (bg){
            if (!bg.visible){
                return;
            }

            const progress = {value: 0};
            const animation = gsap.to(progress,{
                value: 1,
                duration: GSAPAnimationMixer.duration,
                ease: GSAPAnimationMixer.ease,
                onComplete: () => {
                    bg.visible = false;
                }
            });

            mixer.addAnimation(animation);
        }
    }
}

export function backgroundShow () {
    if (config.features.privateHouseMode) return;
    const {cylinderBg} = config.resources.background;
    const mixer = GSAPAnimationMixer.getMixer('exitToOrbit');

    let a = threeJsVariables.background;
    if (!a)
        return;

    if (a) {
        const {position,scale,rotation} = config.resources.background
        a.rotation.set(rotation.x,rotation.y,rotation.z)
        a.position.set(position.x,position.y,position.z)
        a.scale.set(scale.x,scale.y,scale.z)
    }

    // const mixer = GSAPAnimationMixer.getMixer('exitToOrbit');

    if (cylinderBg && cylinderBg === 'sky') {
        if (OrbitDayAndNight.isActiveAndSelected) {
            a.visible = false;
        }

        for (let i=0;i<a.children.length;i++){
            let c = a.children[i];
            // c.layers.enable(1);
            // c.material.transparent = false
            // c.material.opacity = 0
            // c.material.uniforms.t.value = 0.0;

            const animation = gsap.to(c.material.uniforms.t, {
                duration: GSAPAnimationMixer.duration,
                ease: GSAPAnimationMixer.ease,
                value : 0.0
            });

            mixer.addAnimation(animation);
        }

    } else {

        if (OrbitDayAndNight.isActiveAndSelected) {
            a.visible = false;
            return;
        }

        const bg = threeJsVariables.scene.getObjectByName('Background');
        if (bg){

            const progress = {value: 0};

            const animation = gsap.to(progress,{
                value: 1,
                duration: GSAPAnimationMixer.duration,
                ease: GSAPAnimationMixer.ease,
                onStart: () => {
                    bg.visible = true;
                }
            });

            mixer.addAnimation(animation);
        }

    }
}


export class OSMEnv {

    static buildingsMaterials = [];
    static seaMaterial = null;
    static osm = null;
    static preloaded = {};


    constructor () {
        // if (!OrbitDayAndNight.isActive) return;

        // threeJsVariables.scene.add(OSMEnv.osm);

        // if (sceneObjectsGroup.children.length!==0){
        //     let indA = threeJsVariables.scene.children.indexOf(sceneObjectsGroup);
        //     let indB = threeJsVariables.scene.children.indexOf(OSMEnv.osm);
        //     threeJsVariables.scene.children[indB] = sceneObjectsGroup;
        //     threeJsVariables.scene.children[indA] = OSMEnv.osm;
        // } else {
        //     const projectBuildings = threeJsVariables.scene.getObjectByName('Project Buildings');
        //     let indA = threeJsVariables.scene.children.indexOf(projectBuildings);
        //     let indB = threeJsVariables.scene.children.indexOf(OSMEnv.osm);
        //     threeJsVariables.scene.children[indB] = projectBuildings;
        //     threeJsVariables.scene.children[indA] = OSMEnv.osm;
        // }

        // this.addBuildings();
        // this.addSea();
    }

    static addSea(textureLoader) {
        if (Environment.fromBundle()) return;

        const data = OSMEnv.preloaded.sea.elements;
        const { sea } = config.features.dayNight.settings;
        const seaGroup = OSMEnv.osm.getObjectByName('Sea group');

        const coordsInterval = setInterval(() => {
            const lat = Number(config.localization?.latitude);
            const lng = Number(config.localization?.longitude);
            if (lat && lng) {
                clearInterval(coordsInterval);
                buildSea(lat,lng);
            }

        },100);

        function buildSea (lat,lng) {
            const points = [];
            const axis = new Vector3(1,0,0).normalize();

            const allPoints = data.reduce((acc,value) => {
                acc.push(...value.geometry);
                return acc;
            },[]);

            allPoints.sort((a,b) =>  a.lat - b.lat);

            allPoints.forEach(point => {
                const pointSpace = {lat: point.lat, lng: point.lon};
                const vector = latLngAltToVector3(pointSpace,{lat: lat, lng: lng}, axis);
                vector.setY(0);
                points.push(vector);
            });

            if (!points.length) return;

            const angle = points[0].angleTo(points[points.length - 1]);
            const arcAxis = new Vector3(0,-1,0).normalize();
            const tempV = new Vector3().copy(points[points.length - 1]);
            const distance = points[points.length - 1].distanceTo(points[0]);

            const curve = new CatmullRomCurve3([
                points[points.length - 1],
                new Vector3().copy(tempV).applyAxisAngle(arcAxis,angle / 2).setLength(distance / sea.distanceCoefficient),
                points[0]
            ]);

            const curvePoints = curve.getPoints(20);
            points.push(...curvePoints);

            const shapePoints = [];
            points.forEach(point => {
                if (!isNaN(point.x) && !isNaN(point.y) && !isNaN(point.z)) {
                    const v = new Vector2(point.x,point.z);
                    shapePoints.push(v);
                }
            });

            const shape = new Shape(shapePoints);
            const geometry = new ShapeGeometry(shape);

            const uv = new PlanarUV(geometry);
            geometry.setAttribute('uv',uv);

            const seaMesh = new OrbitSea(geometry,{
                loader: textureLoader
            });

            OSMEnv.seaMaterial = seaMesh.material;
            seaMesh.name = 'sea';
            seaGroup.add(seaMesh);
        }
    }

    static addTrees() {
        if (Environment.fromBundle()) return;

        const data = OSMEnv.preloaded.trees.data;
        const textures = OSMEnv.preloaded.trees.textures;
        const treesSettings = config.features.dayNight.settings.trees;
        // const shaders = [];

        const treesGroup = new Group();
        treesGroup.name = 'Trees';
        treesGroup.rotation.set(treesSettings.rotation.x, treesSettings.rotation.y, treesSettings.rotation.z);
        treesGroup.position.set(treesSettings.position.x,treesSettings.position.y,treesSettings.position.z);

        data.forEach((points,index) => {

            const particles = points.reduce((acc,value) => {
                acc.push(value.x,value.y,value.z);
                return acc;
                },[]);

            const pointsGeometry = new BufferGeometry();
            pointsGeometry.setAttribute('position', new Float32BufferAttribute(particles, 3));

            const textureData = textures[index];
            const pointsMaterial = new PointsMaterial({
                size: textureData.size,
                sizeAttenuation: true,
                map: textureData.texture,
                transparent: true,
                alphaTest: 0.8
            });

            pointsMaterial.onBeforeCompile = (shader) => {

                shader.uniforms.treesDensity = { value: config.features.dayNight.settings.trees.fogDensity };

                const vertexVarying = 'varying float zFOG;\n';
                const vertex_position = shader.vertexShader.replace(
                    '#include <project_vertex>',
                    [
                        '#include <project_vertex>',
                        'zFOG = -gl_Position.z;'
                    ].join( '\n' )
                );

                shader.vertexShader = vertexVarying + vertex_position;

                const fragmentVarying = 'varying float zFOG;\n uniform float treesDensity;\n';
                const fragmentMap = shader.fragmentShader.replace(
                    '#include <opaque_fragment>',
                    [
                        '#include <opaque_fragment>',
                        `gl_FragColor.a = smoothstep(0.5,1.0,exp( -treesDensity * treesDensity * zFOG * zFOG ));`
                    ].join( '\n' )
                );

                shader.fragmentShader = fragmentVarying + fragmentMap;
                // shaders.push(shader);
            };

            const mesh = new Points(pointsGeometry, pointsMaterial);
            treesGroup.add(mesh);
        });

        // const params = {
        //     density: config.features.dayNight.settings.trees.fogDensity,
        //     // repeatX: 1,
        //     // repeatY: 1,
        //     // offsetX: 0,
        //     // offsetY:0
        // };
        // const gui = new dat.GUI();
        // gui.width = 450;
        // gui.add(params,'density',0, 0.01, 0.00000001).onChange(v => {
        //     shaders.forEach(m => {
        //         console.log(v);
        //         m.uniforms.treesDensity.value = v;
        //     });
        // });

        OSMEnv.osm.add(treesGroup);

    }

    static addBuildings() {
        if (Environment.fromBundle()) return;
        // const shaders = [];
        const edges = config.features.dayNight.settings.buildingsGroup.edges;
        const colors = new ColorCorrection().getColors('osm');

        OSMEnv.preloaded.osmBuildings.children.forEach(child => {
            const geometry = new BufferGeometry().copy(child.geometry);
            // const alphaUV = geometry.getAttribute('_uva');
            const uv = geometry.getAttribute('uv');
            // geometry.setAttribute('uvA', alphaUV);
            // geometry.deleteAttribute('_uva');
            geometry.setAttribute('uv2',uv);

            const material = new MeshStandardMaterial({
                // color: '#dedede',
                color: new Color().setStyle(colors.color,LinearSRGBColorSpace),
                emissive: new Color().setStyle(colors.emissive,LinearSRGBColorSpace),
                transparent: true,
                depthTest: true,
                depthWrite: true,
                map: OSMEnv.preloaded.osmTextures[child.name],
                lightMapIntensity: 0,
                envMap: OSMEnv.preloaded.envMap,
            });

            if (child.name !== 'Roofs') {
                material.lightMap = OSMEnv.preloaded.osmTextures['lightMap'];
            }

            material.onBeforeCompile = (shader) => {

                shader.uniforms.buildingDensity = { value: config.features.dayNight.settings.buildingsGroup.fogDensity };

                const vertexVarying = 'varying float zFOG;\n';
                const vertex_position = shader.vertexShader.replace(
                    '#include <project_vertex>',
                    [
                        '#include <project_vertex>',
                        'zFOG = -gl_Position.z;'
                    ].join( '\n' )
                );

                shader.vertexShader = vertexVarying + vertex_position;

                const fragmentVarying = 'varying float zFOG;\n uniform float buildingDensity;\n';
                const fragmentMap = shader.fragmentShader.replace(
                    '#include <opaque_fragment>',
                    [
                        '#include <opaque_fragment>',
                        `float gray = gl_FragColor.r * 0.21 + gl_FragColor.g * 0.71 + gl_FragColor.b * 0.07;`,
                        `gl_FragColor.rgb = gl_FragColor.rgb = mix(vec3(gray) * gl_FragColor.rgb,gl_FragColor.rgb,smoothstep(0.5,1.0,exp( -buildingDensity * buildingDensity * zFOG * zFOG )));`,
                        `gl_FragColor.a = smoothstep(0.5,1.0,exp( -buildingDensity * buildingDensity * zFOG * zFOG ));`
                    ].join( '\n' )
                );

                shader.fragmentShader = fragmentVarying + fragmentMap;

                // shaders.push(shader);
            };

            OSMEnv.buildingsMaterials.push(material);

            const mesh = new Mesh(geometry,material);
            mesh.castShadow = true;
            mesh.receiveShadow = true;

            if (edges.active) {
                new OutlineEdges(mesh, {
                    color: edges.color,
                    fogDensity: edges.fogDensity,
                    useFog:true,
                    transparent: true
                });
            }
            mesh.name = child.name;
            OSMEnv.buildingsGroup.add(mesh);
        });

        // const params = {
        //     density: edges.fogDensity,
        //     // repeatX: 1,
        //     // repeatY: 1,
        //     // offsetX: 0,
        //     // offsetY:0
        // };
        // const gui = new dat.GUI();
        // gui.width = 450;
        // gui.add(params,'density',0, 0.01, 0.00000001).onChange(v => {
        //     shaders.forEach(m => {
        //         console.log(v);
        //         m.uniforms.fogDensity.value = v;
        //     });
        // });
        // gui.add(params,'repeatX',0, 2, 0.001).onChange(v => {
        //     OSMEnv.buildingsMaterials.forEach(m => {
        //        m.lightMap?.repeat.setX(v);
        //     });
        // });
        //
        // gui.add(params,'repeatY',0, 2, 0.001).onChange(v => {
        //     OSMEnv.buildingsMaterials.forEach(m => {
        //         m.lightMap?.repeat.setY(v);
        //     });
        // });
        //
        // gui.add(params,'offsetX',0, 2, 0.001).onChange(v => {
        //     OSMEnv.buildingsMaterials.forEach(m => {
        //         m.lightMap?.offset.setX(v);
        //     });
        // });
        //
        // gui.add(params,'offsetY',0, 2, 0.001).onChange(v => {
        //     OSMEnv.buildingsMaterials.forEach(m => {
        //         m.lightMap?.offset.setY(v);
        //     });
        // });
    }

    static update(delta) {
        if (!OrbitDayAndNight.isActive || !OSMEnv.seaMaterial) return;
        OSMEnv.seaMaterial.uniforms.uTime.value = delta;
    }

    static toggle(action) {
        if (Environment.fromBundle()) return;

        OSMEnv.osm.visible = action === 'show';

        // const elements = [];
        //
        // const mixer = GSAPAnimationMixer.registerNewMixer('environment');
        // mixer.addCallback('onStart',() => {
        //     if (action === 'show') {
        //         OSMEnv.osm.visible = true;
        //     }
        // });
        //
        // mixer.addCallback('onComplete',() => {
        //     if (action !== 'show') {
        //         OSMEnv.osm.visible = false;
        //     }
        // });

        // const tl = gsap.timeline({
        //     paused: true,
        //     onStart: () => {
        //         if (action === 'show') {
        //             OSMEnv.osm.visible = true;
        //         }
        //     },
        //     onComplete: () => {
        //         if (action !== 'show') {
        //             OSMEnv.osm.visible = false;
        //         }
        //     }
        // });

        // OSMEnv.osm.traverse(el => {
        //
        //     if (el.isMesh) {
        //         elements.push(el);
        //         // const animation = gsap.to(el.material, {
        //         //     transparent: true,
        //         //     duration: 0.2,
        //         //     opacity: action === 'show' ? 1 : 0
        //         // });
        //         //
        //         // tl.add(animation,0);
        //     }
        // });
        //
        // elements.forEach(el => {
        //     const animation = gsap.to(el.material, {
        //         // transparent: true,
        //         duration: 0.4,
        //         ease: GSAPAnimationMixer.ease,
        //         opacity: action === 'show' ? 1 : 0,
        //         onStart: () => {
        //             el.material.transparent = true;
        //             el.material.needsUpdate = true;
        //         },
        //         // onUpdate: multimaterialNeedsUpdate,
        //         onComplete: () => {
        //             if (el.name === 'sea') {
        //                 el.material.transparent = false;
        //             }
        //         }
        //     });
        //
        //     mixer.addAnimation(animation);
        // });
        //
        // mixer.play();
    }

    static preload(loader,parentGroup) {
        if (Environment.fromBundle() || BmbyMode.active) return;

        if (!OrbitDayAndNight.active) return;

        const { seaGroup, buildingsGroup, localization, ground, trees } = config.features.dayNight.settings;

        const textureLoader = loader.textureLoader;
        const fileLoader = loader.fileLoader;
        const dracoLoader = loader.dracoLoader;

        const mapBoxToken = 'pk.eyJ1IjoiYWJkdWxsYWJlc3QiLCJhIjoiY2tvc2RiZGlwMDBmdjJxbXc2cjRlZTRkcCJ9.-P0yGd4LAGQodEj87GKl-w';
        const mapBoxTileUrl = 'https://api.mapbox.com/styles/v1/mapbox/satellite-v9/tiles/';
        let material = Buildings.getBuildingsArray()[0]?.instances[0].material;

        if (!material) {
            material = Buildings.getBuildingsArray()[0]?.instances[0].children[0].material;
        }

        if (material) {
            if (Array.isArray(material)) {
                OSMEnv.preloaded.envMap = material[0].envMap;
            } else {
                OSMEnv.preloaded.envMap = material.envMap;
            }
        } else {
            OSMEnv.preloaded.envMap = null;
        }

        const sea = new Group();
        sea.name = 'Sea group';
        sea.position.set(seaGroup.position.x,seaGroup.position.y,seaGroup.position.z);
        sea.rotation.set(seaGroup.rotation.x,seaGroup.rotation.y,seaGroup.rotation.z);

        const osmUrl = `${config.cdnRepository}/${config.projectFolder}/${resourcesFolder}/points/`;

        //Preload for buildings
        OSMEnv.preloaded.osmTextures = {};

        if (!Environment.scope?.environments?.Buildings?.useBundle) {
            OSMEnv.buildingsGroup = new Group();
            OSMEnv.buildingsGroup.name = 'Buildings group';
            OSMEnv.buildingsGroup.rotation.set(buildingsGroup.rotation.x, buildingsGroup.rotation.y, buildingsGroup.rotation.z);
            OSMEnv.buildingsGroup.position.set(buildingsGroup.position.x,buildingsGroup.position.y,buildingsGroup.position.z);
            parentGroup.add(OSMEnv.buildingsGroup);

            buildingsGroup.textures.forEach(t => {
                if (!OSMEnv.preloaded.osmTextures[t.type]) {
                    const texture = textureLoader.load(osmUrl + t.path,loadedTexture => threeJsVariables.renderer.initTexture(loadedTexture));
                    texture.wrapS = texture.wrapT = RepeatWrapping;

                    if (Object.keys(t.settings).length) {
                        Object.entries(t.settings).forEach(([key,value]) => {
                            if (key === 'repeat' || key === 'offset') {
                                texture[key].set(value.x,value.y);
                            } else {
                                texture[key] = value;
                            }
                        });
                    }
                    OSMEnv.preloaded.osmTextures[t.type] = texture;
                }
            });

            dracoLoader.load(osmUrl + 'osm.glb',gltf => {
                OSMEnv.preloaded.osmBuildings = gltf.scene.children[0];
                OSMEnv.addBuildings();
            },undefined,() => {
                OSMEnv.preloaded.osmBuildings = null;
            });


        } else {
            parentGroup.add(Environment.scope?.environments?.Buildings.group);
        }

        parentGroup.add(sea);

        new Terrain().loadTerrain(parentGroup);

        //Preload for sea

        fileLoader.load(osmUrl + 'sea.json',sea => {
            OSMEnv.preloaded.sea = JSON.parse(sea);
            OSMEnv.addSea(textureLoader);
        },undefined,() =>{
            OSMEnv.preloaded.sea = null;
        });

        //Preload for trees
        if (trees) {
            OSMEnv.preloaded.trees = {
                data: null,
                textures: []
            };

            trees.textures.forEach(el => {
                OSMEnv.preloaded.trees.textures.push({
                    size: el.size,
                    texture: textureLoader.load(osmUrl + el.path,loadedTexture => threeJsVariables.renderer.initTexture(loadedTexture))
                });
            });

            fileLoader.load(osmUrl + 'trees.json',trees => {
                OSMEnv.preloaded.trees.data = JSON.parse(trees);
                OSMEnv.addTrees();
            },undefined,() =>{
                OSMEnv.preloaded.trees.data = null;
            });
        }

    }

    static updateSeaColor(seaColor, seaWaveColor) {
        if (OSMEnv.seaMaterial) {
            // return;
            OSMEnv.seaMaterial.uniforms.uSeaColor.value.copy(seaColor);
            OSMEnv.seaMaterial.uniforms.uWaveColor.value.copy(seaWaveColor);
        }
    }

    static updateLightMap(intensity) {
        if (OSMEnv.buildingsMaterials.length) {
            OSMEnv.buildingsMaterials.forEach(material => {
                material.lightMapIntensity = intensity * Math.PI;
            });
        }
    }

    static updateEnvMapIntensity(intensity) {
        if (OSMEnv.buildingsMaterials.length && OSMEnv.osm) {

            OSMEnv.osm.traverse(el => {
                if (el.isMesh) {
                    el.material.envMapIntensity = intensity;
                }
            });
        }
    }

    static #lonToTile(lng, zoom) {
        return (lng+180)/360*Math.pow(2,zoom);
    }

    static #latToTile(lat,zoom) {
        return (1-Math.log(Math.tan(lat*Math.PI/180) + 1/Math.cos(lat*Math.PI/180))/Math.PI)/2 *Math.pow(2,zoom);
    }

}

export class EnvironmentToggle {
    static scope = null;

    constructor () {
        this.backgroud360 = null;
        this.backgroudOSM = null;
        EnvironmentToggle.scope = this;
    }

    addToDisplayOption(parent) {
        if (!OrbitDayAndNight.hasBackgroundToggle) return;

        this.main = new BaseElement()
            .addClass('display-option','environment-toggle-container')
            .appendTo(parent)
            .addStyle('display', 'none');
        new BaseElement('span')
            .addClass('environment-toggle__title','language-string')
            .innerHtml(changeLangToCurrent('Background'))
            .addAttribute('data-dictionary','Background')
            .appendTo(this.main.DOMElement);
        
        if (!isMobile) {
            const header = parent.parentNode.querySelector('.header__checker');
            this.preloader = new BaseElement()
                .addClass('background-loader')
                .addStyle('order', '2')
                .appendTo(header, 'beforeBegin');
        }else {
            this.preloader = new BaseElement()
                .addClass('background-loader')
                .appendTo(this.main.DOMElement);
        }

        const mode = new BaseElement()
            .addClass('environment-toggle__mode')
            .appendTo(this.main.DOMElement);
        new BaseElement('span')
            .addClass('environment-toggle__mode-text')
            .innerHtml('360')
            .appendTo(mode.DOMElement);
        this.toggle = new Switch(mode.DOMElement);
        this.toggle.switch.classList.add('environment-toggle-switcher');
        this.toggle.checked = OrbitDayAndNight.osmSelected;
        this.toggle.listener = this.#toggleListener.bind(this);
        new BaseElement('span')
            .addClass('environment-toggle__mode-text')
            .innerHtml('3D')
            .appendTo(mode.DOMElement);
    }

    #toggleListener(e) {
        if (userPrevent())
            return;

        if (e.target.checked) {
            this.toggle.checked = true;
            const preloader = new DreamsPreloader(this.preloader.DOMElement,'background-preloader').absolute().scale(0.2).show();
            setTimeout(() => {
                this.loadOSM(true).then(group => {
                    preloader.remove();
                    if (this.backgroud360) {
                        this.backgroud360.visible = false;
                    }

                    if (Environment.fromBundle()) {
                        Environment.toggle3D('show');
                    } else {
                        PublicLayers.hideLabelsByDistance(false);
                        if (threeJsVariables.scene.children.includes(group)) {
                            group.visible = true;
                        } else {
                            threeJsVariables.scene.add(group);
                            threeJsVariables.renderList.reorder();
                            group.visible = true;
                        }
                    }

                    OrbitDayAndNight.osmSelected = true;
                    Weather.scope.changeClouds('osm');
                    if (!DisplayOptions.scope.orbitDayAndNight) {
                        DisplayOptions.scope.orbitDayAndNight = new OrbitDayAndNight(DisplayOptions.scope.displayOptions,this.data);
                    } else {
                        DisplayOptions.scope.orbitDayAndNight.restorePreviouslyPreset();
                    }

                    DisplayOptions.scope.orbitDayAndNight.currentTimeMainContainer.classList.remove('hidden');
                    DisplayOptions.scope.orbitDayAndNight.currentTimeMainContainer.classList.remove('day-night-master-hidden');
                    DisplayOptions.displayOptionsVisibility('update');

                });
            },10);
        } else {
            this.toggle.checked = false;
            const preloader = new DreamsPreloader(this.preloader.DOMElement,'background-preloader').absolute().scale(0.2).show();
            setTimeout(() => {
                this.load360(true).then(group => {
                    preloader.remove();

                    if (Environment.fromBundle()) {
                        Environment.toggle3D('hide');
                    } else {
                        this.backgroudOSM.visible = false;
                        PublicLayers.hideLabelsByDistance(true);
                    }

                    if (threeJsVariables.scene.children.includes(group)) {
                        group.visible = true;
                    } else {
                        threeJsVariables.scene.add(group);
                        threeJsVariables.renderList.reorder();
                        group.visible = true;
                    }

                    if ((settingsData.activeViewMode !== ViewMode.Orbit) && (config.resources.background.cylinderBg !== 'sky')) {
                        group.visible = false;
                    }

                    if (Environment.getActivePublicLayer() && Environment.getActivePublicLayer().id !== 'poi') {
                        const layer = document.querySelector('.public-layer__btn.poi') ||
                            document.querySelector(`.public-layer__btn[data-public-layer="${Environment.getActivePublicLayer().id}"]`);
                        Environment.scope.publicLayersHandler({ target: layer });
                    }
                    if (PublicLayers.activeLayer && PublicLayers.activeLayer.id !== 'poi') {
                        const layer = document.querySelector('.public-layer__btn.poi') ||
                            document.querySelector(`.public-layer__btn[data-public-layer="${PublicLayers.activeLayer.id}"]`);
                        PublicLayers.scope.clickHandler({ target: layer });
                    }

                    OrbitDayAndNight.osmSelected = false;
                    OrbitDayAndNight.presetsToggle('reset');
                    Weather.scope.changeClouds('360');
                    if (DisplayOptions.scope.orbitDayAndNight) {
                        DisplayOptions.scope.orbitDayAndNight.currentTimeMainContainer.classList.add('hidden');
                        DisplayOptions.scope.orbitDayAndNight.currentTimeMainContainer.classList.add('day-night-master-hidden');
                        DisplayOptions.scope.orbitDayAndNight.resetToDefault();
                    }
                    DisplayOptions.displayOptionsVisibility('update');
                });
            },10);
        }
    }

    load360(preloader = false,loader) {
        return new Promise((resolve,reject) => {
            try {
                if (this.backgroud360) {
                    preloader && this.preloader.innerHtml('');
                    // if (Environment.isActive()) {
                    //     const terrain = Environment.getLayer('Terrain');
                    //     terrain && terrain.hide();
                    // }
                    resolve(this.backgroud360);
                } else if (threeJsVariables.background) {
                    this.backgroud360 = threeJsVariables.background;
                    // if (Environment.isActive()) {
                    //     const terrain = Environment.getLayer('Terrain');
                    //     terrain && terrain.hide();
                    // }
                    resolve(this.backgroud360);
                } else {
                    preloader && new DreamsPreloader(this.preloader.DOMElement,'background-preloader').absolute().scale(0.2).show();
                    let newLoader;
                    if (loader) {
                        newLoader = loader;
                    } else {
                        newLoader = new Loader();
                    }

                    newLoader.addCallback = () => {
                        this.backgroud360 = threeJsVariables.background;
                        preloader && this.preloader.innerHtml('');
                        threeJsVariables.tasks.run();
                        resolve(this.backgroud360);
                    };

                    const textureLoader = newLoader.textureLoader;
                    OrbitDayAndNight.osmSelected = false;
                    prepareSphere(textureLoader);
                }
            }catch (e) {
                reject(e);
            }
        });
    }

    loadOSM(preloader = false,loader) {
        userPrevent(true);
        return new Promise((resolve, reject) => {
            if (Environment.fromBundle()) {
                userPrevent(false);
                preloader && this.preloader.innerHtml('');
                resolve();
                return;
            }

            try {
                if (this.backgroudOSM) {
                    userPrevent(false);
                    preloader && this.preloader.innerHtml('');
                    // if (Environment.isActive()) {
                    //     const terrain = Environment.getLayer('Terrain');
                    //     terrain && terrain.show();
                    // }
                    resolve(this.backgroudOSM);
                } else {
                    preloader && new DreamsPreloader(this.preloader.DOMElement).absolute().scale(0.2).show();
                    let newLoader;
                    const callback = () => {
                        console.log('3d assets loaded');
                        this.backgroudOSM = OSMEnv.osm;
                        preloader && this.preloader.innerHtml('');
                        // dynamicFogToggle();
                        // setFogParams({
                        //     active: true
                        // });
                        resolve(this.backgroudOSM);
                        const waitingDisplayOptions = setInterval(() => {
                            if (DisplayOptions.scope && this.data) {
                                clearInterval(waitingDisplayOptions);
                                if (!DisplayOptions.scope.orbitDayAndNight) {
                                    DisplayOptions.scope.orbitDayAndNight = new OrbitDayAndNight(DisplayOptions.scope.displayOptions,this.data);
                                } else {
                                    DisplayOptions.scope.orbitDayAndNight.currentTimeMainContainer.classList.remove('hidden');
                                    DisplayOptions.scope.orbitDayAndNight.currentTimeMainContainer.classList.remove('day-night-master-hidden');
                                }
                            }
                            userPrevent(false);
                        },100);
                    };

                    if (loader) {
                        newLoader = loader;
                    } else {
                        newLoader = new Loader();
                    }

                    newLoader.addCallback = callback;
                    OSMEnv.osm = new Group();
                    OSMEnv.osm.name = 'OSM env';
                    OrbitDayAndNight.preload(newLoader,OSMEnv.osm);
                    OSMEnv.preload(newLoader,OSMEnv.osm);
                }
            }catch (e) {
                reject(e);
            }
        });
    }

    static preloadOSM() {
        if (!OrbitDayAndNight.isActive || OrbitDayAndNight.isActiveAndSelected || Environment.fromBundle()) return;

        const loader = new Loader();
        OSMEnv.osm = new Group();
        OSMEnv.osm.name = 'OSM env';
        OrbitDayAndNight.preload(loader,OSMEnv.osm);
        OSMEnv.preload(loader,OSMEnv.osm);
        EnvironmentToggle.scope.backgroudOSM = OSMEnv.osm;
    }

    load(loader) {
        if (!OrbitDayAndNight.hasBackgroundToggle || userPrevent()) return;

        let background;
        if (OrbitDayAndNight.isActiveAndSelected) {
            background = this.loadOSM(false,loader);
        } else {
            background = this.load360(false,loader);
        }

        background.then(group => {
            if (!group) return;
            if (!threeJsVariables.scene.children.includes(group)) {
                threeJsVariables.scene.add(group);
                threeJsVariables.renderList.reorder();
                console.log('end loading')
            }
        });
    }

    set response(data) {
        this.data = data;
    }
}


// export function needsBackgroundAnimation () {
//     const {cylinderBg,type} = config.resources.background;
//     return cylinderBg === 'sky' || type === 'projection';
// }
